"use client"

import { useState, useEffect, useRef } from "react"
import { Link as LinkIcon, Paperclip, X } from "lucide-react"

const CreateFolderModal = ({ isOpen, onClose, onCreateFolder }) => {
  const [folderName, setFolderName] = useState("")
  const [selectedColor, setSelectedColor] = useState("#4299E1")
  const [selectedFile, setSelectedFile] = useState(null)
  const [fileLabel, setFileLabel] = useState("")
  const fileInputRef = useRef(null)

  const colors = [
    { name: "Blue",   value: "#4299E1" },
    { name: "Orange", value: "#ED8936" },
    { name: "Purple", value: "#9F7AEA" },
    { name: "Teal",   value: "#38B2AC" },
    { name: "Pink",   value: "#ED64A6" },
    { name: "Indigo", value: "#6366F1" },
    { name: "Cyan",   value: "#06B6D4" },
    { name: "Slate",  value: "#64748B" },
  ]

  useEffect(() => {
    if (isOpen) {
      resetState()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const resetState = () => {
    setFolderName("")
    setSelectedColor("#4299E1")
    setSelectedFile(null)
    setFileLabel("")
    if (fileInputRef.current) fileInputRef.current.value = ""
  }

  const handleCreateFolder = () => {
    if (folderName.trim()) {
      onCreateFolder(folderName.trim(), selectedColor, selectedFile, fileLabel.trim())
      resetState()
      onClose()
    }
  }

  const handleCancel = () => {
    resetState()
    onClose()
  }

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      // Only submit if we’re not focused in the file label input to avoid surprises
      const active = document.activeElement
      const isTypingInTextField =
        active?.tagName === "INPUT" && active?.getAttribute("type") === "text"
      if (!isTypingInTextField) handleCreateFolder()
    } else if (e.key === "Escape") {
      handleCancel()
    }
  }

  const openFilePicker = () => fileInputRef.current?.click()

  const stripExt = (name = "") => {
    const idx = name.lastIndexOf(".")
    return idx > 0 ? name.slice(0, idx) : name
  }

  const onFileChosen = (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    const MAX_MB = 25
    if (file.size > MAX_MB * 1024 * 1024) {
      alert(`File is too large. Max ${MAX_MB} MB.`)
      e.target.value = ""
      return
    }
    setSelectedFile(file)
    // Seed label from filename (no extension) if not already set
    setFileLabel((prev) => prev || stripExt(file.name))
  }

  const removeFile = () => {
    setSelectedFile(null)
    setFileLabel("")
    if (fileInputRef.current) fileInputRef.current.value = ""
  }

  if (!isOpen) return null

  const formatBytes = (bytes) => {
    if (bytes === undefined || bytes === null) return ""
    const units = ["B", "KB", "MB", "GB"]
    let i = 0
    let n = bytes
    while (n >= 1024 && i < units.length - 1) {
      n /= 1024
      i++
    }
    return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${units[i]}`
  }


  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        className="bg-white rounded-lg p-6 w-96 max-w-[90vw] shadow-xl"
        onKeyDown={handleKeyPress}
      >
        <h3 className="text-lg font-semibold mb-4 text-[#2D3748]">Create New Folder</h3>

        <div className="mb-4">
          <label className="block text-sm font-medium text-[#4A5568] mb-2">Folder Name</label>
          <input
            type="text"
            value={folderName}
            onChange={(e) => setFolderName(e.target.value)}
            placeholder="Enter folder name..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#4299E1] focus:border-transparent"
            maxLength={50}
            autoFocus
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-[#4A5568] mb-2">Folder Color</label>
          <div className="flex gap-3 flex-wrap">
            {colors.map((color) => (
              <button
                type="button"
                key={color.value}
                onClick={() => setSelectedColor(color.value)}
                className={`w-6 h-6 rounded-full transition-all duration-200 hover:scale-110 ${
                  selectedColor === color.value
                    ? "ring-2 ring-offset-2"
                    : "hover:ring-1 hover:ring-gray-300 hover:ring-offset-1"
                }`}
                style={{ backgroundColor: color.value }}
                title={color.name}
                aria-label={`Select ${color.name}`}
              />
            ))}
          </div>
        </div>

        {/* Attach base file */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-[#4A5568] mb-2">Base File (optional)</label>
          <div className="flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={openFilePicker}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-gray-300 text-[#2D3748] bg-white hover:bg-[#EBF8FF] hover:text-[#4299E1] transition-colors"
            >
              <Paperclip className="w-4 h-4" />
              <span>Attach base file</span>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={onFileChosen}
              accept="*/*"
            />
          </div>

          {selectedFile && (
            <div className="mt-3 space-y-2">
              {/* File chip */}
              <div className="flex items-center justify-between rounded-md bg-gray-50 border border-gray-200 px-3 py-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[#2D3748] truncate">
                    {selectedFile.name}
                  </p>
                  <p className="text-xs text-[#718096]">
                    {formatBytes(selectedFile.size)} · {selectedFile.type || "Unknown type"}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={removeFile}
                  className="p-1 rounded hover:bg-gray-200 transition-colors"
                  aria-label="Remove attached file"
                  title="Remove"
                >
                  <X className="w-4 h-4 text-[#4A5568]" />
                </button>
              </div>

              {/* File display name (alias) */}
              <div>
                <label className="block text-xs font-medium text-[#4A5568] mb-1">
                  Display Name (shown in folder)
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={fileLabel}
                    onChange={(e) => {
                      const next = e.target.value.slice(0, 60) // hard cap
                      setFileLabel(next)
                    }}
                    placeholder="e.g., Project Brief, SRS v1, Client Contract"
                    className="w-full px-3 py-2 pr-12 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#4299E1] focus:border-transparent"
                    maxLength={60}
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#718096]">
                    {fileLabel.length}/60
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Preview */}
      {folderName.trim() && ( <div className="mb-4 p-3 bg-gray-50 rounded-lg"> <p className="text-xs text-[#718096] mb-2">Preview:</p> 
      <div className="bg-white rounded-lg p-3 border-l-4 shadow-sm" style={{ borderLeftColor: selectedColor }} > 
        <div className="flex items-center justify-between"> <div> 
          <h4 className="font-semibold text-[#2D3748] text-sm">{folderName}</h4> 
          <p className="text-[#718096] text-xs mt-1">0 tasks</p> 
          </div> {/* Show link icon only if a file is attached */} 
          {selectedFile ? ( <LinkIcon className="w-4 h-4" style={{ color: selectedColor }} /> ) : null} 
          </div> 
          </div> 
          </div> 
        
        )}
        <div className="flex gap-2 justify-end">
          <button
            onClick={handleCancel}
            className="px-4 py-2 text-[#4A5568] hover:text-[#4299E1] hover:bg-[#EBF8FF] rounded-md font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleCreateFolder}
            disabled={!folderName.trim()}
            className="px-4 py-2 bg-[#4299E1] text-white rounded-md font-medium hover:bg-[#3182CE] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Create
          </button>
        </div>
      </div>
    </div>
  )
}

export default CreateFolderModal
